import React, { useState, useEffect } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import axios from 'axios';
import InternalClientStageManager from './InternalClientStageManager';
import MeetingScheduler from './MeetingScheduler';
import { AvailabilityBuilder } from './AvailabilitySection';
import useSSE from '../hooks/useSSE';
import { API_BASE_URL } from '../config/api';
import { hasPermission } from '../utils/permissions';

const API = API_BASE_URL;

/**
 * ClientStageTab — recruiter view of client interview stages.
 * Responsive grid with "New for Review" first column, stage columns,
 * and a "placed" drop zone at the bottom. Mirrors client-side pipeline board.
 */
const ClientStageTab = ({ pipelineId, onCardClick, hideManageStagesButton = false }) => {
  const [stages, setStages] = useState([]);
  const [unassigned, setUnassigned] = useState([]);
  const [placed, setPlaced] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showStageManager, setShowStageManager] = useState(false);
  // Drag-to-disqualify drop-zone confirmation (in addition to the per-card
  // inline status dropdown — both ways to disqualify are available).
  const [dqPrompt, setDqPrompt] = useState(null);
  const [dqReason, setDqReason] = useState('');
  const [dqSubmitting, setDqSubmitting] = useState(false);

  // Listen for the "open client stage manager" event dispatched by
  // SearchDetailPage's tool-styled Manage Stages button in the sub-tab
  // bar (when Interview Stages is the active sub-tab). Mirrors the
  // banyak:open_stage_manager pattern PipelineBoard uses for internal
  // stages — avoids threading a ref up.
  useEffect(() => {
    const handler = () => setShowStageManager(true);
    window.addEventListener('banyak:open_client_stage_manager', handler);
    return () => window.removeEventListener('banyak:open_client_stage_manager', handler);
  }, []);
  const [cardMeetings, setCardMeetings] = useState({});
  const [meetingRefresh, setMeetingRefresh] = useState(0);
  const [schedulerCard, setSchedulerCard] = useState(null);
  const [availCard, setAvailCard] = useState(null);

  const token = localStorage.getItem('authToken');
  const authHeaders = { Authorization: `Bearer ${token}` };

  useEffect(() => { loadClientStages(); }, [pipelineId]);

  const loadClientStages = async () => {
    try {
      const res = await axios.get(`${API}/api/pipelines/${pipelineId}/client-stages`, { headers: authHeaders });
      setStages(res.data.stages || []);
      setUnassigned(res.data.unassigned || []);
      // Load placed candidates
      try {
        const placedRes = await axios.get(`${API}/api/pipelines/${pipelineId}/cards?status=placed`, { headers: authHeaders });
        setPlaced(placedRes.data || []);
      } catch (e) { /* placed endpoint may not exist for all pipelines */ }
    } catch (err) { console.error('Error loading client stages:', err); }
    finally { setLoading(false); }
  };

  // Real-time updates via SSE
  useSSE(pipelineId, {
    onCardMoved: () => loadClientStages(),
    onRatingUpdated: () => loadClientStages(),
    onStatusChanged: () => loadClientStages(),
    onFeedbackAdded: () => loadClientStages(),
    onMeetingUpdated: () => { loadClientStages(); setMeetingRefresh(n => n + 1); },
    onStageChanged: () => loadClientStages(),
    onCandidateReactivated: () => loadClientStages(),
  });

  // Load meetings for all visible cards
  useEffect(() => {
    const allCards = [...unassigned, ...stages.flatMap(s => s.candidates)];
    if (allCards.length === 0) return;
    const loadMeetings = async () => {
      const meetingsMap = {};
      await Promise.all(allCards.map(async (c) => {
        try {
          const res = await fetch(`${API}/api/calendar/meetings/${c.card_id}`, { headers: { Authorization: `Bearer ${token}` } });
          if (res.ok) { const data = await res.json(); if (data.length > 0) meetingsMap[c.card_id] = data; }
        } catch {}
      }));
      setCardMeetings(meetingsMap);
    };
    loadMeetings();
  }, [stages, unassigned, meetingRefresh]);

  const handleDragEnd = async (result) => {
    const { destination, source, draggableId } = result;
    if (!destination) return;
    if (destination.droppableId === source.droppableId && destination.index === source.index) return;
    const cardId = parseInt(draggableId.replace('client-card-', ''));

    // Placed drop zone
    if (destination.droppableId === 'placed') {
      const candidate = [...unassigned, ...stages.flatMap(s => s.candidates)].find(c => c.card_id === cardId);
      if (!window.confirm(`Mark ${candidate?.name || 'this candidate'} as Placed?\n\nThis will move them to the placed list.`)) return;
      try {
        await axios.put(`${API}/api/cards/${cardId}/stage`, { stage_id: 'placed' }, { headers: { ...authHeaders, 'Content-Type': 'application/json' } });
        await loadClientStages();
      } catch (err) { console.error('Error placing candidate:', err); }
      return;
    }

    // Disqualify drop zone → prompt for reason (optional), then reject.
    if (destination.droppableId === 'disqualify') {
      const candidate = [...unassigned, ...stages.flatMap(s => s.candidates)].find(c => c.card_id === cardId);
      setDqReason('');
      setDqPrompt({ cardId, name: candidate?.name || 'this candidate' });
      return;
    }

    const newStageId = parseInt(destination.droppableId.replace('client-stage-', ''));
    try {
      await axios.put(`${API}/api/cards/${cardId}/client-stage`, { client_stage_id: newStageId }, { headers: { ...authHeaders, 'Content-Type': 'application/json' } });
      await loadClientStages();
    } catch (err) { console.error('Error moving candidate:', err); alert('Failed to move candidate'); }
  };

  const confirmDisqualify = async () => {
    if (!dqPrompt) return;
    setDqSubmitting(true);
    try {
      await axios.put(`${API}/api/client/cards/${dqPrompt.cardId}/status`,
        { status: 'rejected', rejection_reason: dqReason.trim() || null },
        { headers: { ...authHeaders, 'Content-Type': 'application/json' } });
      setDqPrompt(null);
      setDqReason('');
      await loadClientStages();
    } catch (err) {
      console.error('Error disqualifying candidate:', err);
      alert('Failed to disqualify candidate: ' + (err.response?.data?.error || err.message));
    } finally {
      setDqSubmitting(false);
    }
  };

  const handleClick = (candidate) => {
    if (onCardClick) {
      onCardClick({ candidate_id: candidate.candidate_id, card_id: candidate.card_id, name: candidate.name, current_title: candidate.current_title, current_company: candidate.current_company });
    }
  };

  const getRatingDisplay = (rating) => {
    if (!rating) return null;
    return (
      <div className="flex items-center gap-0.5">
        {[1,2,3,4,5].map(s => (
          <span key={s} className={`text-[11px] leading-none ${s <= rating ? 'text-yellow-400' : 'text-gray-300'}`}>★</span>
        ))}
      </div>
    );
  };

  // Compact card used in all columns
  const CandidateCard = ({ candidate, provided, snapshot }) => {
    const [imgErr, setImgErr] = React.useState(false);
    const [statusOpen, setStatusOpen] = React.useState(false);
    const [updating, setUpdating] = React.useState(false);
    const [reasonOpen, setReasonOpen] = React.useState(null);
    const [reason, setReason] = React.useState('');
    const showPic = candidate.avatar_url && !imgErr;
    const ini = candidate.name?.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();

    const statusConfig = {
      pending: { label: 'Pending', bg: 'bg-yellow-100 text-yellow-700', hoverBg: 'hover:bg-yellow-50' },
      approved: { label: 'Approved', bg: 'bg-green-100 text-green-700', hoverBg: 'hover:bg-green-50' },
      rejected: { label: 'Rejected', bg: 'bg-red-100 text-red-700', hoverBg: 'hover:bg-red-50' },
      withdrew: { label: 'Withdrew', bg: 'bg-purple-100 text-purple-700', hoverBg: 'hover:bg-purple-50' },
    };
    const cardStatus = candidate.status || 'pending';
    const currentStatus = statusConfig[cardStatus];

    const handleStatusChange = async (newStatus, reasonText) => {
      setUpdating(true);
      try {
        await axios.put(`${API}/api/client/cards/${candidate.card_id}/status`, { status: newStatus, rejection_reason: reasonText || null }, { headers: authHeaders });
      } catch (err) { console.error('Status update failed:', err); }
      finally {
        await loadClientStages();
        setUpdating(false); setStatusOpen(false); setReasonOpen(null); setReason('');
      }
    };

    return (
    <div ref={provided?.innerRef} {...(provided?.draggableProps || {})} {...(provided?.dragHandleProps || {})}
      onClick={() => !snapshot?.isDragging && handleClick(candidate)}
      className={`bg-white border rounded-lg px-2.5 py-2 cursor-pointer hover:shadow-md transition-shadow ${snapshot?.isDragging ? 'shadow-lg ring-2 ring-blue-200' : ''}`}>
      <div className="flex items-center gap-2">
        {showPic ? (
          <img src={candidate.avatar_url} alt="" className="w-12 h-12 rounded-full object-cover flex-shrink-0 border border-gray-200" onError={() => setImgErr(true)} />
        ) : (
          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 text-white text-sm font-bold flex items-center justify-center flex-shrink-0">
            {ini}
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-1">
            <p className="text-xs font-medium text-gray-900 truncate">{candidate.name}</p>
            {candidate.candidate_code && <span className="text-[9px] font-mono text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded border border-blue-200 flex-shrink-0">{candidate.candidate_code}</span>}
          </div>
          <p className="text-[10px] text-gray-500 truncate mt-0.5">{candidate.current_title}{candidate.current_company ? ` · ${candidate.current_company}` : ''}</p>
          <div className="flex items-center justify-between mt-1">
            <div className="flex items-center gap-1.5">
              {getRatingDisplay(candidate.rating) || <span className="text-[10px] text-gray-300">No rating</span>}
              <div className="relative" onMouseDown={(e) => e.stopPropagation()} onPointerDown={(e) => e.stopPropagation()}>
                <button onClick={(e) => { e.stopPropagation(); setStatusOpen(!statusOpen); setReasonOpen(null); setReason(''); }}
                  disabled={updating}
                  className={`text-[9px] font-medium px-1.5 py-0.5 rounded cursor-pointer transition-all ${currentStatus.bg} ${updating ? 'opacity-50' : 'hover:ring-1 hover:ring-offset-1 hover:ring-gray-300'}`}>
                  {updating ? '...' : currentStatus.label}
                </button>
                {statusOpen && (
                  <>
                    <div className="fixed inset-0 z-10" onClick={(e) => { e.stopPropagation(); setStatusOpen(false); }} />
                    <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-20 min-w-[120px] py-1">
                      {Object.entries(statusConfig).filter(([key]) => key !== cardStatus).map(([key, cfg]) => (
                        <button key={key} onClick={(e) => {
                          e.stopPropagation();
                          if (key === 'rejected' || key === 'withdrew') { setReasonOpen(key); setStatusOpen(false); }
                          else handleStatusChange(key);
                        }}
                          className={`w-full text-left px-3 py-1.5 text-xs ${cfg.hoverBg} flex items-center gap-2`}>
                          <span className={`w-2 h-2 rounded-full ${key === 'approved' ? 'bg-green-500' : key === 'rejected' ? 'bg-red-500' : key === 'withdrew' ? 'bg-purple-500' : 'bg-yellow-500'}`} />
                          {cfg.label}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
            {candidate.last_updated && (
              <span className="text-[9px] text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">
                {(() => {
                  const hrs = Math.floor((Date.now() - new Date(candidate.last_updated).getTime()) / (1000 * 60 * 60));
                  if (hrs < 1) return '<1h';
                  if (hrs < 24) return `${hrs}h`;
                  const days = Math.floor(hrs / 24);
                  if (days < 7) return `${days}d`;
                  const weeks = Math.floor(days / 7);
                  return `${weeks}w`;
                })()}
              </span>
            )}
          </div>
        </div>
      </div>
      {/* Reason input for reject/withdraw */}
      {reasonOpen && (
        <div className={`mt-2 p-2 rounded-lg border space-y-1.5 ${reasonOpen === 'withdrew' ? 'bg-purple-50 border-purple-200' : 'bg-red-50 border-red-200'}`}
          onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()} onPointerDown={(e) => e.stopPropagation()}>
          <textarea value={reason} onChange={(e) => setReason(e.target.value)}
            placeholder={reasonOpen === 'withdrew' ? 'Reason for withdrawal (optional)...' : 'Reason for rejection (optional)...'}
            className={`w-full text-[10px] px-2 py-1 border rounded focus:outline-none focus:ring-1 resize-none bg-white ${reasonOpen === 'withdrew' ? 'border-purple-200 focus:ring-purple-400' : 'border-red-200 focus:ring-red-400'}`}
            rows={2} autoFocus />
          <div className="flex justify-end gap-1">
            <button onClick={(e) => { e.stopPropagation(); setReasonOpen(null); setReason(''); }} className="text-[9px] px-2 py-0.5 text-gray-500 border border-gray-200 rounded hover:bg-gray-50">Cancel</button>
            <button onClick={(e) => { e.stopPropagation(); handleStatusChange(reasonOpen, reason.trim() || null); }}
              className={`text-[9px] px-2 py-0.5 text-white rounded font-medium ${reasonOpen === 'withdrew' ? 'bg-purple-600 hover:bg-purple-700' : 'bg-red-600 hover:bg-red-700'}`}>
              Confirm
            </button>
          </div>
        </div>
      )}
      {/* Meeting row */}
      {(() => {
        const meetings = cardMeetings[candidate.card_id];
        const next = meetings?.find(m => new Date(m.start_time) > new Date());
        return (
          <div className="mt-1.5 -mx-1 -mb-0.5 rounded-b-md bg-gray-50 border-t border-gray-100 flex items-stretch overflow-hidden">
            {/* Left half — Request availability */}
            <div onClick={(e) => { e.stopPropagation(); setAvailCard({ card_id: candidate.card_id, candidate_id: candidate.candidate_id, name: candidate.name }); }}
              className="flex-1 px-2 py-1 flex items-center gap-1.5 hover:bg-emerald-50 transition-colors cursor-pointer border-r border-gray-100">
              <svg className="w-3 h-3 text-emerald-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
              <span className="text-[9px] text-gray-500 truncate">Availability</span>
            </div>
            {/* Right half — Schedule meeting */}
            <div onClick={(e) => { e.stopPropagation(); if (hasPermission('schedule_meetings')) setSchedulerCard({ card_id: candidate.card_id, candidate_id: candidate.candidate_id, name: candidate.name, email: candidate.email }); }}
              className="flex-1 px-2 py-1 flex items-center gap-1.5 hover:bg-blue-50 transition-colors cursor-pointer">
              <svg className="w-3 h-3 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
              {next ? (
                <span className="text-[9px] text-gray-600 truncate">
                  {new Date(next.start_time).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} · {new Date(next.start_time).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}
                  {next.meeting_url && <span className="text-blue-500 ml-1">📹</span>}
                </span>
              ) : (
                <span className="text-[9px] text-gray-400">Schedule</span>
              )}
            </div>
          </div>
        );
      })()}
    </div>
    );
  };

  if (loading) return <div className="flex items-center justify-center py-12"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div></div>;

  const totalCandidates = unassigned.length + stages.reduce((s, st) => s + st.candidates.length, 0);

  return (
    <div className="p-4 space-y-3">
      {/* Toolbar */}
      <div className="flex items-center justify-between">
        <span className="text-xs text-gray-500">{totalCandidates} candidates in client process</span>
        {!hideManageStagesButton && (
          <button onClick={() => setShowStageManager(true)}
            className="px-2.5 py-1 text-[11px] bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center gap-1">
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" /></svg>
            Manage Stages
          </button>
        )}
      </div>

      <DragDropContext onDragEnd={handleDragEnd}>
        {/* Responsive grid: New for Review + stage columns */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
          {/* New for Review (unassigned) */}
          <Droppable droppableId="unassigned">
            {(provided, snapshot) => (
              <div ref={provided.innerRef} {...provided.droppableProps}
                className={`rounded-lg border transition-colors ${snapshot.isDraggingOver ? 'border-blue-400 bg-blue-50' : 'border-blue-200 bg-blue-50/50'}`}>
                <div className="px-3 py-2 border-b border-blue-200">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-blue-800">New for Review</span>
                    <span className="text-[10px] text-blue-600 bg-blue-100 px-1.5 py-0.5 rounded-full font-medium">{unassigned.length}</span>
                  </div>
                </div>
                <div className="p-2 space-y-1.5 min-h-[120px]">
                  {unassigned.map((c, i) => (
                    <Draggable key={c.card_id} draggableId={`client-card-${c.card_id}`} index={i}>
                      {(prov, snap) => <CandidateCard candidate={c} provided={prov} snapshot={snap} />}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                  {unassigned.length === 0 && !snapshot.isDraggingOver && (
                    <p className="text-[10px] text-blue-400 text-center py-4">No new candidates</p>
                  )}
                </div>
              </div>
            )}
          </Droppable>

          {/* Stage columns */}
          {stages.map(stage => (
            <Droppable key={stage.id} droppableId={`client-stage-${stage.id}`}>
              {(provided, snapshot) => (
                <div ref={provided.innerRef} {...provided.droppableProps}
                  className={`rounded-lg border transition-colors ${snapshot.isDraggingOver ? 'border-blue-400 bg-blue-50' : 'border-gray-200 bg-white'}`}>
                  <div className="px-3 py-2 border-b bg-gray-50">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1.5">
                        <span className="text-xs font-semibold text-gray-700">{stage.name}</span>
                        {stage.is_terminal && <span className="text-[8px] px-1 py-0.5 bg-purple-100 text-purple-600 rounded">Terminal</span>}
                      </div>
                      <span className="text-[10px] text-gray-500 bg-gray-200 px-1.5 py-0.5 rounded-full font-medium">{stage.candidates.length}</span>
                    </div>
                  </div>
                  <div className="p-2 space-y-1.5 min-h-[120px]">
                    {stage.candidates.map((c, i) => (
                      <Draggable key={c.card_id} draggableId={`client-card-${c.card_id}`} index={i}>
                        {(prov, snap) => <CandidateCard candidate={c} provided={prov} snapshot={snap} />}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                    {stage.candidates.length === 0 && !snapshot.isDraggingOver && (
                      <p className="text-[10px] text-gray-400 text-center py-4">Drop here</p>
                    )}
                  </div>
                </div>
              )}
            </Droppable>
          ))}
        </div>

        {/* Placed + Disqualify drop zones — side-by-side on md+, stacked on mobile */}
        <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
          <Droppable droppableId="placed">
            {(provided, snapshot) => (
              <div ref={provided.innerRef} {...provided.droppableProps}
                className={`border-2 border-dashed rounded-lg px-4 py-3 text-center transition-all ${
                  snapshot.isDraggingOver ? 'border-green-500 bg-green-100' : 'border-green-300 bg-green-50/50'
                }`}>
                <div className="flex items-center justify-center gap-2">
                  <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="text-xs font-medium text-green-800">
                    {snapshot.isDraggingOver ? 'Drop to mark as Placed' : `Drag here to place (${placed.length} placed)`}
                  </span>
                </div>
                {provided.placeholder}
              </div>
            )}
          </Droppable>

          <Droppable droppableId="disqualify">
            {(provided, snapshot) => (
              <div ref={provided.innerRef} {...provided.droppableProps}
                className={`border-2 border-dashed rounded-lg px-4 py-3 text-center transition-all ${
                  snapshot.isDraggingOver ? 'border-red-500 bg-red-100' : 'border-red-300 bg-red-50/50'
                }`}>
                <div className="flex items-center justify-center gap-2">
                  <svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                  </svg>
                  <span className="text-xs font-medium text-red-800">
                    {snapshot.isDraggingOver ? 'Drop to disqualify' : 'Drag here to disqualify'}
                  </span>
                </div>
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </div>
      </DragDropContext>

      {/* Empty state */}
      {stages.length === 0 && unassigned.length === 0 && (
        <div className="text-center py-12 text-sm text-gray-400">No client interview stages configured yet.</div>
      )}

      {showStageManager && (
        <InternalClientStageManager pipelineId={pipelineId} onClose={() => setShowStageManager(false)} onUpdate={loadClientStages} />
      )}

      {/* Disqualify reason modal — opens when a card is dropped on the Disqualify zone */}
      {dqPrompt && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => !dqSubmitting && setDqPrompt(null)}>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-sm mx-4 p-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-3">
              <svg className="w-5 h-5 text-red-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
              </svg>
              <h3 className="text-sm font-semibold text-gray-900">Disqualify {dqPrompt.name}?</h3>
            </div>
            <textarea value={dqReason} onChange={(e) => setDqReason(e.target.value)} autoFocus
              placeholder="Reason for disqualification (optional)..."
              className="w-full text-xs px-2.5 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-red-400 resize-none" rows={3} />
            <div className="flex items-center justify-end gap-2 mt-3">
              <button onClick={() => { setDqPrompt(null); setDqReason(''); }} disabled={dqSubmitting}
                className="text-xs px-3 py-1.5 text-gray-600 border border-gray-200 rounded hover:bg-gray-50 disabled:opacity-50">Cancel</button>
              <button onClick={confirmDisqualify} disabled={dqSubmitting}
                className="text-xs px-3 py-1.5 bg-red-600 text-white rounded hover:bg-red-700 font-medium disabled:opacity-50">
                {dqSubmitting ? 'Disqualifying…' : 'Disqualify'}
              </button>
            </div>
          </div>
        </div>
      )}

      {schedulerCard && (
        <MeetingScheduler
          isOpen={true}
          onClose={() => setSchedulerCard(null)}
          cardId={schedulerCard.card_id}
          candidateId={schedulerCard.candidate_id}
          candidateName={schedulerCard.name}
          candidateEmail={schedulerCard.email}
          pipelineId={pipelineId}
          pipelineName={stages[0]?.pipeline_name || ''}
          onMeetingCreated={(meeting) => {
            setCardMeetings(prev => ({ ...prev, [schedulerCard.card_id]: [...(prev[schedulerCard.card_id] || []), meeting] }));
          }}
        />
      )}
      {availCard && (
        <AvailabilityBuilder
          candidateId={availCard.candidate_id}
          candidateName={availCard.name}
          pipelineId={pipelineId}
          pipelineName={stages[0]?.pipeline_name || ''}
          onClose={() => setAvailCard(null)}
          onCreated={() => {}}
        />
      )}
    </div>
  );
};

export default ClientStageTab;
